import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { NewsItem, subscribeToNewsItems, markNewsItemAsRead, hideNewsItem } from '../../services/newsItemService';
import { getNewsItemPageSize } from '../../services/settingsService';
import { Topic } from '../../services/topicService';
import NewsItemComponent from '../news-item/NewsItemComponent';
import { NewsItemsSkeletonList } from '../../components/SkeletonLoader';
import './NewsItemsList.css';
import FlipMove from 'react-flip-move';

interface NewsItemsListProps {
  selectedTopic: Topic | null; // Keep for backward compatibility, but not used
  onBackToAllNews: () => void;
  onTopicClick: (topic: Topic) => void;
  user?: any;
  onShowNotification?: (message: string, type?: 'success' | 'info') => void;
  selectedTab?: 'feed' | 'saved';
}

const NewsItemsList: React.FC<NewsItemsListProps> = ({ selectedTopic, onBackToAllNews, onTopicClick, user, onShowNotification, selectedTab = 'feed' }) => {
  const { topicName } = useParams<{ topicName?: string }>();
  const loadMoreRef = React.useRef<HTMLButtonElement | null>(null);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [currentPageSize, setCurrentPageSize] = useState(0); // Track current page size - starts at 0 until settings load
  const [basePageSize, setBasePageSize] = useState(0); // Base page size for increments - loaded from settings
  const [notifications, setNotifications] = useState<Array<{id: string, message: string, index: number}>>([]);

  // Convert topicName from URL back to display format
  const getTopicDisplayName = (kebabTopic: string) => {
    return kebabTopic.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Helper function to remove notification after 2 seconds
  const removeNotification = (notificationId: string) => {
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    }, 2000);
  };

  const handleMarkAsRead = async (newsItemId: string) => {
    try {
      // Find the index of the item
      const index = newsItems.findIndex(item => item.id === newsItemId);
      
      // Immediately remove the item
      setNewsItems(prevItems => prevItems.filter(item => item.id !== newsItemId));
      
      // Add notification at the position
      setNotifications(prev => {
        const newNotifications = [...prev, {id: `notification-${newsItemId}`, message: 'Article marked as read', index}];
        return newNotifications.sort((a, b) => a.index - b.index);
      });
      
      // Remove notification after 2 seconds
      removeNotification(`notification-${newsItemId}`);
      
      // Send the mark as read request to the backend
      await markNewsItemAsRead(newsItemId);
    } catch (error) {
      setError('Failed to mark news item as read. Please try again.');
    }
  };

  const handleHide = async (newsItemId: string) => {
    try {
      // Immediately remove the item from the local state
      setNewsItems(prevItems => prevItems.filter(item => item.id !== newsItemId));
      
      // Send the hide request to the backend
      await hideNewsItem(newsItemId);
    } catch (error) {
      setError('Failed to hide news item. Please try again.');
      
      // If the API call fails, we should reload the data to restore the item
      // This will happen automatically through the subscription, but we could force it if needed
    }
  };

  const handleSaveForLater = async (newsItemId: string) => {
    try {
      // Find the index of the item
      const index = newsItems.findIndex(item => item.id === newsItemId);
      
      // Immediately remove the item
      setNewsItems(prevItems => prevItems.filter(item => item.id !== newsItemId));
      
      // Add notification at the position
      setNotifications(prev => {
        const newNotifications = [...prev, {id: `notification-${newsItemId}`, message: 'Article saved for later', index}];
        return newNotifications.sort((a, b) => a.index - b.index);
      });
      
      // Remove notification after 2 seconds
      removeNotification(`notification-${newsItemId}`);
      
      // The backend save operation is handled by the NewsItemComponent
      // No additional backend call needed here since it's done in the component
    } catch (error) {
      setError('Failed to save news item for later. Please try again.');
    }
  };

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      setLoadingMore(true);
      // Increase page size by one page worth of items
      const newPageSize = currentPageSize + basePageSize;
      setCurrentPageSize(newPageSize);
      
      // Re-subscribe with larger page size
      const unsubscribe = subscribeToNewsItems((newNewsItems, hasMoreItems, total) => {
        setNewsItems(newNewsItems);
        setHasMore(hasMoreItems || false);
        setLoadingMore(false);
        unsubscribe(); // Clean up this temporary subscription
      }, user, newPageSize, topicName);
    }
  };

  // Load settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const pageSize = await getNewsItemPageSize();
        setBasePageSize(pageSize);
        setCurrentPageSize(pageSize);
      } catch (error) {
        // Keep default values if settings fail to load
        setBasePageSize(5);
        setCurrentPageSize(5);
      }
    };
    
    loadSettings();
  }, []);

  useEffect(() => {
    // Only start subscription after settings are loaded (basePageSize > 0)
    if (basePageSize === 0) {
      return; // Wait for settings to load
    }
    
    const effectiveTopicName = selectedTab === 'saved' ? 'saved' : topicName;
    
    const unsubscribe = subscribeToNewsItems((newNewsItems, hasMoreItems, total) => {
      setNewsItems(newNewsItems);
      setHasMore(hasMoreItems || false);
      setLoading(false);
      setError(null);
    }, user, currentPageSize, effectiveTopicName);

    // Error handling for the subscription
    const handleError = (error: Error) => {
      setError('Failed to load news items. Please refresh the page.');
      setLoading(false);
    };

    // Clean up subscription on unmount
    return () => {
      try {
        unsubscribe();
      } catch (error) {
        handleError(error as Error);
      }
    };
  }, [user, currentPageSize, basePageSize, topicName, selectedTab]); // Added topicName and selectedTab as dependencies

  // Auto-click Load More button when it comes into view
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;
    const button = loadMoreRef.current;
    if (!button) return;
    let clicked = false;
    const observer = new window.IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !clicked) {
          clicked = true;
          button.click();
        }
      });
    }, { threshold: 0.5 });
    observer.observe(button);
    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore]);

  // Filter news items based on selected topic
  // No longer needed since filtering is done server-side
  const filteredNewsItems = newsItems;

  // Combine news items and notifications, inserting notifications at their positions
  const items: Array<NewsItem | {id: string, message: string, index: number}> = [];
  let notificationIndex = 0;
  filteredNewsItems.forEach((item, i) => {
    while (notificationIndex < notifications.length && notifications[notificationIndex].index <= i) {
      items.push(notifications[notificationIndex]);
      notificationIndex++;
    }
    items.push(item);
  });
  while (notificationIndex < notifications.length) {
    items.push(notifications[notificationIndex]);
    notificationIndex++;
  }

  // Show loading while settings are being loaded or news items are being fetched
  if (loading || basePageSize === 0) {
    return (
      <div className="news-items-list">
        <NewsItemsSkeletonList count={basePageSize > 0 ? basePageSize : 5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="news-items-list">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="news-items-list">
      {topicName && (
        <div className="filter-header">
          <h2>{getTopicDisplayName(topicName)}</h2>
          <button className="see-all-button" onClick={onBackToAllNews}>
            See All
          </button>
        </div>
      )}
      
      {filteredNewsItems.length === 0 ? (
        <div className="empty-state">
          {topicName ? (
            <p>No news items found for "{getTopicDisplayName(topicName)}". Try searching for other topics!</p>
          ) : selectedTab === 'saved' ? (
            <p>No saved items yet. Save some articles to see them here!</p>
          ) : user ? (
            <p>No news items yet. Add topics and wait for the scheduled search to find news!</p>
          ) : (
            <p>No common news items available at the moment. Please check back later!</p>
          )}
        </div>
      ) : (
        <FlipMove
          duration={300}
          easing="ease-out"
          appearAnimation="fade"
          enterAnimation="fade"
          leaveAnimation="fade" 
        >
          {items.map((item) => {
            if ('message' in item) {
              return (
                <div key={item.id} className="inline-notification">
                  {item.message}
                </div>
              );
            } else {
              return (
                <div key={item.id}>
                  <NewsItemComponent 
                    newsItem={item as NewsItem} 
                    onTopicClick={onTopicClick}
                    onMarkAsRead={handleMarkAsRead}
                    onHide={handleHide}
                    onSaveForLater={handleSaveForLater}
                    user={user}
                  />
                </div>
              );
            }
          })}
        </FlipMove>
      )}
      
      {/* Load More Button - Show for all users (both logged in and logged out) */}
      {hasMore && (
        <div className="load-more-controls">
          <button 
            className="load-more-button"
            onClick={handleLoadMore}
            disabled={loading || loadingMore}
            ref={loadMoreRef}
          >
            {(loading || loadingMore) ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Loading News...
              </>
            ) : (
              <>
                <i className="fas fa-chevron-down"></i>
                More News
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default NewsItemsList;